// Sistemas → Gestión de Autenticación → Países (/seguridad/paises).
// Catálogo GLOBAL financiero.paises (compartido por todo el sistema): América
// sembrada con su moneda funcional. Cada empresa se asigna a un país en la
// pantalla Empresas; la moneda funcional de la empresa se deriva de aquí
// (trigger). Lectura por REST; escritura SOLO por RPC paises_guardar (nivel 5).
import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Globe, Pencil, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { Campo, Modal, Switch, fieldCls, fin } from './shared';
import { SortTh, useSort } from '@/features/inventario/shared';

interface Pais {
  id: number; codigo: string; nombre: string; nacionalidad: string;
  codigo_telefono: string | null; moneda: string | null; activo: boolean; orden: number;
}

const vacio = { codigo: '', nombre: '', nacionalidad: '', codigo_telefono: '', moneda: '', activo: true, orden: 100 };
type Form = typeof vacio;

export function Paises() {
  const qc = useQueryClient();
  const [busq, setBusq] = useState('');
  const [form, setForm] = useState<Form | null>(null);
  const [esNuevo, setEsNuevo] = useState(false);

  const { data: rows = [], isFetching } = useQuery<Pais[]>({
    queryKey: ['adm-paises'],
    queryFn: () => fin.list<Pais>('paises', '?select=*&order=nombre'),
  });

  const filtrados = rows.filter((p) => {
    const q = busq.trim().toLowerCase();
    return !q || p.nombre.toLowerCase().includes(q) || p.codigo.toLowerCase().includes(q)
      || (p.moneda ?? '').toLowerCase().includes(q);
  });
  const accessor = useCallback((r: Pais, k: string) => {
    if (k === 'orden') return r.orden;
    if (k === 'activo') return r.activo ? 1 : 0;
    return (r[k as keyof Pais] as string | null) ?? null;
  }, []);
  const sort = useSort(filtrados, accessor, 'nombre');

  const guardar = useMutation({
    mutationFn: async () => {
      await apiClient.post('/rpc/paises_guardar', {
        _data: {
          codigo: form!.codigo, nombre: form!.nombre, nacionalidad: form!.nacionalidad,
          codigo_telefono: form!.codigo_telefono, moneda: form!.moneda,
          activo: form!.activo, orden: Number(form!.orden) || 100,
        },
      }, { headers: { 'Content-Profile': 'financiero' } });
    },
    onSuccess: () => { toast.success('País guardado'); setForm(null); void qc.invalidateQueries({ queryKey: ['adm-paises'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const abrir = (p?: Pais) => {
    setEsNuevo(!p);
    setForm(p ? {
      codigo: p.codigo, nombre: p.nombre, nacionalidad: p.nacionalidad,
      codigo_telefono: p.codigo_telefono ?? '', moneda: p.moneda ?? '', activo: p.activo, orden: p.orden,
    } : { ...vacio });
  };

  return (
    <div className="p-6 space-y-4 max-w-4xl">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Globe className="w-5 h-5" /> Países</h1>
        <input className={`${fieldCls} !w-64 ml-auto`} placeholder="Buscar país / código / moneda…" value={busq} onChange={(e) => setBusq(e.target.value)} />
        <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nuevo país</Button>
      </div>
      <p className="text-sm text-muted-foreground">
        Catálogo global del sistema (América precargada). Cada empresa elige su país en <b>Empresas</b>;
        la <b>moneda funcional</b> de la empresa se toma de aquí automáticamente. Los países inactivos no
        se pueden asignar a empresas nuevas.
      </p>

      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-muted-foreground border-b bg-muted/30">
            <th className="py-2 px-3 text-right w-10">N°</th>
            <SortTh col="codigo" sort={sort} className="px-3">Código</SortTh>
            <SortTh col="nombre" sort={sort} className="px-3">País</SortTh>
            <SortTh col="nacionalidad" sort={sort} className="px-3">Nacionalidad</SortTh>
            <SortTh col="codigo_telefono" sort={sort} className="px-3">Tel.</SortTh>
            <SortTh col="moneda" sort={sort} className="px-3">Moneda</SortTh>
            <SortTh col="activo" sort={sort} className="px-3">Activo</SortTh>
            <th className="py-2 px-3" />
          </tr></thead>
          <tbody>
            {sort.sorted.map((p, i) => (
              <tr key={p.codigo} className={`border-b last:border-0 ${!p.activo ? 'opacity-50' : ''}`}>
                <td className="py-1.5 px-3 text-right text-muted-foreground">{i + 1}</td>
                <td className="py-1.5 px-3 font-mono font-medium">{p.codigo}</td>
                <td className="py-1.5 px-3">{p.nombre}</td>
                <td className="py-1.5 px-3 text-muted-foreground">{p.nacionalidad}</td>
                <td className="py-1.5 px-3 text-muted-foreground">{p.codigo_telefono ?? '—'}</td>
                <td className="py-1.5 px-3 font-mono">{p.moneda ?? '—'}</td>
                <td className="py-1.5 px-3">{p.activo ? 'Sí' : 'No'}</td>
                <td className="py-1.5 px-3 text-right">
                  <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(p)}>
                    <Pencil className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            {!sort.sorted.length && <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">{isFetching ? 'Cargando…' : 'Sin países.'}</td></tr>}
          </tbody>
        </table>
      </div>

      {form && (
        <Modal title={esNuevo ? 'Nuevo país' : `Editar ${form.nombre}`} onClose={() => setForm(null)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Código ISO (2 letras)">
                <input className={`${fieldCls} uppercase`} maxLength={2} value={form.codigo} disabled={!esNuevo}
                  onChange={(e) => setForm({ ...form, codigo: e.target.value.toUpperCase() })} placeholder="EC" />
              </Campo>
              <Campo label="Moneda (ISO 4217)">
                <input className={`${fieldCls} uppercase`} maxLength={3} value={form.moneda}
                  onChange={(e) => setForm({ ...form, moneda: e.target.value.toUpperCase() })} placeholder="USD" />
              </Campo>
              <Campo label="Nombre" className="col-span-2">
                <input className={fieldCls} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} autoFocus={!esNuevo} />
              </Campo>
              <Campo label="Nacionalidad"><input className={fieldCls} value={form.nacionalidad} onChange={(e) => setForm({ ...form, nacionalidad: e.target.value })} placeholder="ecuatoriana" /></Campo>
              <Campo label="Código telefónico"><input className={fieldCls} value={form.codigo_telefono} onChange={(e) => setForm({ ...form, codigo_telefono: e.target.value })} placeholder="+593" /></Campo>
            </div>
            <Switch checked={form.activo} onChange={(v) => setForm({ ...form, activo: v })} label="Activo (asignable a empresas)" />
            <p className="text-xs text-muted-foreground">La moneda definida aquí se vuelve la <b>moneda funcional</b> de las empresas de este país (redondeos, POS y reportes). Cambiarla no re-procesa documentos ya emitidos.</p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setForm(null)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.codigo || !form.nombre} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
